import React, { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import ButtonLoader from "../../UI/Loaders/Loaders";

// Required for pdfjs worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface IDocumentPreview {
  url: string;
  /** e.g. "image/png", "application/pdf", a Word or Office mime type. */
  mimeType: string;
  /** Px, or a CSS length. The document viewer modal keeps its fixed 1000×800. */
  width?: number | string;
  height?: number | string;
  /** How wide a PDF page draws, in px. */
  pageWidth?: number;
}

const isOffice = (mimeType: string) =>
  mimeType.includes("word") ||
  mimeType.includes("excel") ||
  mimeType.includes("presentation");

/**
 * A document drawn in place: images as they are, PDFs page by page, Word,
 * Excel and PowerPoint through Office's online viewer, anything else through
 * the browser with a download link to fall back on.
 */
export default function DocumentPreview({
  url,
  mimeType,
  width = "100%",
  height = "100%",
  pageWidth = 900,
}: IDocumentPreview) {
  const [numPages, setNumPages] = useState<number | null>(null);

  if (mimeType.startsWith("image/")) {
    return (
      <img
        src={url}
        alt="Document preview"
        style={{ maxWidth: width, maxHeight: height, objectFit: "contain" }}
      />
    );
  }

  if (mimeType === "application/pdf") {
    return (
      <div style={{ width, height, overflow: "auto" }}>
        <Document
          file={url}
          onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
          loading={<ButtonLoader />}
        >
          {Array.from(new Array(numPages ?? 0), (_unused, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth}
            />
          ))}
        </Document>
      </div>
    );
  }

  if (isOffice(mimeType)) {
    return (
      <iframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url,
        )}`}
        width={width}
        height={height}
        style={{ border: "none" }}
        title="Office Document Viewer"
      />
    );
  }

  return (
    <object
      type={mimeType}
      data={url}
      width={width}
      height={height}
      style={{ objectFit: "contain" }}
    >
      <p>
        Cannot preview this file.{" "}
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download instead
        </a>
      </p>
    </object>
  );
}
